import { ReactNode, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import FFLogo from '@forwardflow/assets/ff-logo.png'
import { Button, Icon } from './ui'
import { FooterCtx, IFooter } from './footer-context'
import { Circle, TMotion } from './motion'
import { useFirstRun } from './store'

/** The purple Forward Flow mark, as it is. */
export const Logo = () => (
   <Link to="/start" className="flex items-center min-w-0" aria-label="Forward Flow">
      <img src={FFLogo} alt="Forward Flow" className="w-11 h-11 rounded-xl" />
   </Link>
)

export const Account = () => {
   const { owner, auth } = useFirstRun()
   const initial = (auth ? owner.name : 'FF').charAt(0).toUpperCase()
   return (
      <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[var(--fr-surface-2)] text-[14px] font-semibold" aria-label="Account">
         {initial}
      </span>
   )
}

export const TopBar = ({ title, center, right }: { title?: ReactNode; center?: ReactNode; right?: ReactNode }) => (
   <header className="h-[84px] shrink-0 border-b border-[var(--fr-border)] flex items-center justify-between px-[30px]">
      <div className="flex items-center gap-4 min-w-0">
         <Logo />
         {title && <span className="text-[15px] font-semibold text-[var(--fr-text)] truncate">{title}</span>}
      </div>
      <div className="flex items-center">{center}</div>
      <div className="flex items-center gap-4">
         {right}
         <Account />
      </div>
   </header>
)

/** The dashboard's side navigation: an icon rail on the left, like the one the app already has. */
export const SideNav = () => {
   const { pathname } = useLocation()
   const items = [
      { to: '/start/dashboard', label: 'Dashboard', icon: 'home' },
      { to: '/start/home', label: 'Campaigns', icon: 'chart' },
      { to: '/start/conversations', label: 'Conversations', icon: 'message' },
      { to: '/start/numbers', label: 'Numbers', icon: 'phone' },
      { to: '/start/settings', label: 'Settings', icon: 'settings' },
   ]
   return (
      <aside className="hidden md:flex w-[92px] shrink-0 border-r border-[var(--fr-border)] flex-col items-center justify-center gap-2" aria-label="Main" data-testid="side-nav">
         {items.map((i) => {
            const on = pathname === i.to
            return (
               <Link key={i.to} to={i.to} aria-current={on ? 'page' : undefined} className={cn('w-[68px] py-2.5 rounded-xl flex flex-col items-center gap-1 text-[11px] font-semibold transition-colors', on ? 'bg-[var(--fr-blue-tint)] text-[var(--fr-blue-dark)]' : 'text-[var(--fr-text-3)] hover:bg-[var(--fr-surface-2)] hover:text-[var(--fr-text)]')}>
                  <Icon name={i.icon} size={20} />
                  {i.label}
               </Link>
            )
         })}
      </aside>
   )
}

/** A dashboard page: top bar, side navigation, scrolling content. */
export const AppShell = ({ children, testId }: { children: ReactNode; testId?: string }) => (
   <div className="fr fixed inset-0 flex flex-col overflow-hidden" data-testid={testId}>
      <TopBar />
      <div className="flex-1 min-h-0 flex">
         <SideNav />
         <main className="flex-1 min-w-0 min-h-0 overflow-y-auto fr-scroll">{children}</main>
      </div>
   </div>
)

/**
 * The frame from the low-fidelity layout: top bar, a left panel, the content
 * column. The actions sit at the bottom of the content column, Back left,
 * primary right, at the same spot on every screen.
 */
export const Frame = ({ children, title, rail, right }: { children: ReactNode; title?: ReactNode; rail?: ReactNode; right?: ReactNode }) => {
   const [footer, setFooter] = useState<IFooter | null>(null)
   const ctx = useMemo(() => ({ set: setFooter }), [])
   return (
      <FooterCtx.Provider value={ctx}>
         <div className="fr fixed inset-0 flex flex-col overflow-hidden" data-testid="fr-frame">
            <TopBar title={title} right={right} />
            <div className="flex-1 min-h-0 flex">
               {rail && <aside className="hidden md:flex w-[295px] shrink-0 border-r border-[var(--fr-border)] flex-col">{rail}</aside>}
               <main className="flex-1 min-w-0 flex flex-col">
                  <div className="flex-1 min-h-0">{children}</div>
                  <div className={cn('fr-footer h-[96px] md:h-[116px] shrink-0 flex items-center justify-between gap-3 px-4 md:px-[36px] transition-opacity', !footer && 'opacity-0 pointer-events-none')} data-testid="fr-footer">
                     <div className="w-12 md:w-[180px] shrink-0">
                        {footer?.back && (
                           <Button variant="secondary" size="lg" className="w-12 md:w-[180px] !px-0 md:!px-7" onClick={footer.back} testId="fr-back">
                              <Icon name="arrow-left" size={16} /> <span className="hidden md:inline">Back</span>
                           </Button>
                        )}
                     </div>
                     <div className="hidden md:block min-w-0 text-center px-4">
                        {footer?.hint && <p className="text-[13px] text-[var(--fr-text-3)] truncate">{footer.hint}</p>}
                        {footer?.secondary && (
                           <button type="button" onClick={footer.secondary.onClick} className="text-[13px] font-semibold text-[var(--fr-blue)] hover:underline mt-0.5" data-testid="fr-secondary">
                              {footer.secondary.label}
                           </button>
                        )}
                     </div>
                     <div className="flex-1 md:flex-none md:w-[326px] shrink-0 min-w-0">
                        {footer && (
                           <Button size="lg" full onClick={footer.onAction} disabled={footer.disabled || footer.loading} testId={footer.testId || 'fr-action'} variant={footer.variant === 'dark' ? 'black' : 'primary'}>
                              <span className="truncate">{footer.loading ? 'One moment' : footer.action}</span> {!footer.loading && <Icon name={footer.icon || 'chevrons'} size={20} />}
                           </Button>
                        )}
                     </div>
                  </div>
               </main>
            </div>
         </div>
      </FooterCtx.Provider>
   )
}

/**
 * One screen: the flat icon in its disc, the question, one line of
 * context, then the one thing to do, all centered in the content column.
 */
export const Screen = ({ children, kind, stepKey, direction = 1, title, lede, width = 'md' }: { children: ReactNode; kind: TMotion; stepKey: string; direction?: 1 | -1; title: ReactNode; lede?: ReactNode; width?: 'sm' | 'md' | 'lg' }) => (
   <div className="h-full overflow-y-auto overflow-x-hidden fr-scroll">
      <AnimatePresence mode="wait" initial={false}>
         <motion.section
            key={stepKey}
            initial={{ opacity: 0, x: 24 * direction }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 * direction }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="fr-screen min-h-full flex flex-col items-center px-8 pt-[32px] pb-4"
            data-testid={'screen-' + stepKey}
         >
            <Circle kind={kind} />
            <h1 className="fr-title mt-4 text-[32px] md:text-[38px] leading-[1.1] font-semibold tracking-[-0.02em] text-center [text-wrap:balance]">{title}</h1>
            {lede && <p className="fr-lede mt-3 text-[16px] leading-6 text-[var(--fr-text-2)] text-center max-w-[62ch] [text-wrap:pretty]">{lede}</p>}
            <div className={cn('fr-body mt-7 w-full', width === 'sm' && 'max-w-[440px]', width === 'md' && 'max-w-[560px]', width === 'lg' && 'max-w-[760px]')}>{children}</div>
         </motion.section>
      </AnimatePresence>
   </div>
)
